// One-shot migration of the two retired client-local instance shadow
// sets onto real server state.
//
// Before the server grew a /leave endpoint, "Discard" for a joined
// (non-creator) participant could only hide the row in a localStorage
// dismissed set. The seat stayed occupied on the server, so the same
// match kept showing in the Queue & Invites panel while being invisible
// in Your Matches. This migration replays the user's recorded intent
// ("get rid of this match") against the server: abandon what they
// created, leave what they joined. The pendingResumeInvites set needs
// no replay. The server already derives it from live invitation rows
// (pending_invite_count). So its key is simply dropped.
//
// Both keys are removed afterwards regardless of per-instance failures:
// any instance the replay couldn't retire just resurfaces in the lists,
// where every row now has a working server-backed Discard/Leave action.

import { listInstances, abandonInstance, leaveInstance } from './instancesApi';

const DISMISSED_KEY = 'valkenhall.dismissedInstances.v1';
const PENDING_RESUME_KEY = 'valkenhall.pendingResumeInvites.v1';

export async function migrateLocalInstanceState(profileId) {
  if (typeof localStorage === 'undefined' || !profileId) return;

  try { localStorage.removeItem(PENDING_RESUME_KEY); } catch {}

  let dismissedIds;
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    if (!raw) return;
    const arr = JSON.parse(raw);
    dismissedIds = new Set(Array.isArray(arr) ? arr : []);
  } catch {
    dismissedIds = new Set();
  }

  try {
    if (dismissedIds.size > 0) {
      const instances = await listInstances();
      const toRetire = (instances || []).filter((inst) => (
        dismissedIds.has(inst.id)
        && inst.phase !== 'completed' && inst.phase !== 'abandoned'
      ));
      for (const inst of toRetire) {
        const me = (inst.participants || []).find((p) => p.userId === profileId);
        if (!me) continue;
        try {
          if (me.role === 'creator') await abandonInstance(inst.id);
          else await leaveInstance(inst.id);
        } catch (err) {
          console.warn('[migration] could not retire dismissed instance', inst.id, err?.message || err);
        }
      }
    }
    localStorage.removeItem(DISMISSED_KEY);
  } catch (err) {
    // listInstances failed (offline, pre-auth). Keep the key so the
    // next login retries the replay.
    console.warn('[migration] dismissed-instance replay failed:', err?.message || err);
  }
}
